import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Dlq as DlqService, DlqEntry, DlqSummary } from '../../core/services/dlq';

const STAGE_LABELS: Record<string, string> = {
  T1: 'T1 · Scrape',
  T2: 'T2 · OCR',
  T3: 'T3 · Chunk & Embed',
  T4: 'T4 · Enrich',
};

@Component({
  selector: 'app-dlq',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar],
  templateUrl: './dlq.html',
  styleUrl: './dlq.css'
})
export class DlqPage implements OnInit {
  private dlqService = inject(DlqService);

  entries = signal<DlqEntry[]>([]);
  summary = signal<DlqSummary | null>(null);
  total = signal(0);
  page = signal(1);
  pageSize = 50;
  loading = signal(false);
  error = signal<string | null>(null);
  busyIds = signal<Set<string>>(new Set());

  stageFilter = signal<string>('');
  statusFilter = signal<string>('Pending');

  selectedIds = signal<Set<string>>(new Set());
  bulkBusy = signal(false);

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.error.set(null);

    this.dlqService.summary().subscribe({ next: s => this.summary.set(s) });

    this.dlqService
      .list({
        stage: this.stageFilter() || undefined,
        status: this.statusFilter() || undefined,
        page: this.page(),
        pageSize: this.pageSize,
      })
      .subscribe({
        next: res => {
          this.entries.set(res.items);
          this.total.set(res.total);
          this.loading.set(false);
          this.selectedIds.set(new Set());
        },
        error: () => {
          this.error.set('Unable to load DLQ entries.');
          this.loading.set(false);
        }
      });
  }

  onFilterChange(): void {
    this.page.set(1);
    this.refresh();
  }

  stageLabel(stage: string): string {
    return STAGE_LABELS[stage] ?? stage;
  }

  isBusy(id: string): boolean {
    return this.busyIds().has(id);
  }

  private setBusy(id: string, busy: boolean): void {
    const next = new Set(this.busyIds());
    if (busy) next.add(id); else next.delete(id);
    this.busyIds.set(next);
  }

  retry(entry: DlqEntry): void {
    this.setBusy(entry.id, true);
    this.dlqService.retry(entry.id).subscribe({
      next: () => { this.setBusy(entry.id, false); this.refresh(); },
      error: () => { this.setBusy(entry.id, false); this.error.set(`Failed to retry '${entry.fileName}'.`); }
    });
  }

  dismiss(entry: DlqEntry): void {
    this.setBusy(entry.id, true);
    this.dlqService.dismiss(entry.id).subscribe({
      next: () => { this.setBusy(entry.id, false); this.refresh(); },
      error: () => { this.setBusy(entry.id, false); this.error.set(`Failed to dismiss '${entry.fileName}'.`); }
    });
  }

  retryBatch(batchId: string): void {
    this.setBusy(batchId, true);
    this.dlqService.retryBatch(batchId).subscribe({
      next: () => { this.setBusy(batchId, false); this.refresh(); },
      error: () => { this.setBusy(batchId, false); this.error.set('Failed to retry batch.'); }
    });
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelected(id: string): void {
    const next = new Set(this.selectedIds());
    if (next.has(id)) next.delete(id); else next.add(id);
    this.selectedIds.set(next);
  }

  get allOnPageSelected(): boolean {
    const entries = this.entries();
    return entries.length > 0 && entries.every(e => this.selectedIds().has(e.id));
  }

  toggleSelectAllOnPage(): void {
    const entries = this.entries();
    if (this.allOnPageSelected) {
      this.selectedIds.set(new Set());
    } else {
      this.selectedIds.set(new Set(entries.map(e => e.id)));
    }
  }

  bulkRetrySelected(): void {
    const ids = Array.from(this.selectedIds());
    if (ids.length === 0 || this.bulkBusy()) return;
    this.bulkBusy.set(true);
    this.dlqService.bulkRetry(ids).subscribe({
      next: res => {
        this.bulkBusy.set(false);
        this.refresh();
      },
      error: () => {
        this.bulkBusy.set(false);
        this.error.set('Failed to retry selected documents.');
      }
    });
  }

  bulkDismissSelected(): void {
    const ids = Array.from(this.selectedIds());
    if (ids.length === 0 || this.bulkBusy()) return;
    this.bulkBusy.set(true);
    this.dlqService.bulkDismiss(ids).subscribe({
      next: () => {
        this.bulkBusy.set(false);
        this.refresh();
      },
      error: () => {
        this.bulkBusy.set(false);
        this.error.set('Failed to dismiss selected documents.');
      }
    });
  }

  /** Retries every pending entry matching the current filters, not just
   * this page -- for "fix the whole pattern at once" once whatever caused
   * a stage's failures is resolved. */
  retryAllFiltered(): void {
    if (this.bulkBusy()) return;
    this.bulkBusy.set(true);
    this.dlqService.retryFiltered({ stage: this.stageFilter() || undefined }).subscribe({
      next: () => {
        this.bulkBusy.set(false);
        this.refresh();
      },
      error: () => {
        this.bulkBusy.set(false);
        this.error.set('Failed to retry all matching documents.');
      }
    });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.pageSize));
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages()) return;
    this.page.set(p);
    this.refresh();
  }
}
